import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import OptimizedImage from '../../components/base/OptimizedImage';
import Breadcrumbs from '../../components/feature/Breadcrumbs';

const EmpresaPage = () => {
  useEffect(() => {
    document.title = 'Empresa - PLADUREX | Especialistas en Pladur Sabadell';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Conoce PLADUREX, empresa especializada en instalación de pladur en Sabadell. Profesionales con experiencia en tabiques, techos y aislamientos. Calidad y limpieza garantizadas.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'empresa pladur Sabadell, instaladores profesionales, pladuristas Sabadell, reformas pladur');
    }

    // Schema.org JSON-LD
    const aboutPageSchema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "PLADUREX",
        "description": "Empresa especializada en instalación de pladur en Sabadell. Ofrecemos soluciones profesionales para tabiques, trasdosados, falsos techos y sistemas de aislamiento acústico y térmico.",
        "telephone": "+34629501941",
        "areaServed": [
          {
            "@type": "City",
            "name": "Sabadell"
          },
          {
            "@type": "AdministrativeArea",
            "name": "Vallès Occidental"
          }
        ],
        "priceRange": "€€"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(aboutPageSchema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Calidad',
      description: 'Trabajamos con materiales de primera calidad y seguimos los estándares más exigentes en cada instalación.'
    },
    {
      icon: 'ri-time-line',
      title: 'Puntualidad',
      description: 'Cumplimos los plazos acordados y respetamos tu tiempo. Planificación precisa y ejecución eficiente.'
    },
    {
      icon: 'ri-brush-line',
      title: 'Limpieza',
      description: 'Mantenemos el espacio de trabajo ordenado y limpio durante toda la obra. Recogida diaria de escombros.'
    },
    {
      icon: 'ri-customer-service-line',
      title: 'Atención',
      description: 'Asesoramiento personalizado y comunicación constante. Resolvemos todas tus dudas en cada fase.'
    }
  ];

  const process = [
    {
      number: '01',
      title: 'Visita y Medición',
      description: 'Nos desplazamos a tu vivienda, oficina o local en Sabadell para evaluar el proyecto. Tomamos medidas precisas y analizamos las necesidades específicas de tu espacio.',
      icon: 'ri-ruler-line'
    },
    {
      number: '02',
      title: 'Propuesta Detallada',
      description: 'Elaboramos un presupuesto completo sin compromiso. Incluye materiales, mano de obra, plazos y opciones de aislamiento. Todo claro y transparente.',
      icon: 'ri-file-list-line'
    },
    {
      number: '03',
      title: 'Ejecución Profesional',
      description: 'Instalación con técnicas profesionales y materiales de calidad. Trabajamos con orden y limpieza, minimizando las molestias si la vivienda está habitada.',
      icon: 'ri-tools-line'
    },
    {
      number: '04',
      title: 'Remates y Acabados',
      description: 'Tratamiento de juntas, lijado y acabado liso listo para pintar. Revisión final para garantizar que todo quede perfecto antes de la entrega.',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  return (
    <div className="min-h-screen bg-dark-bg text-light-text">
      <Header />
      <Breadcrumbs />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <OptimizedImage
            src="https://public.readdy.ai/ai/img_res/34dce840e807bf0c4ecc91704e155606.jpg"
            alt="Equipo profesional PLADUREX"
            loading="lazy"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-dark-bg"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              Especialistas en <span className="text-primary-orange">Pladur</span>
            </h1>
            <p className="text-xl md:text-2xl text-light-muted max-w-3xl mx-auto">
              Profesionales con experiencia en instalación de pladur en Sabadell y comarca
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-8 text-center">
                Quiénes <span className="text-primary-orange">somos</span>
              </h2>
              <div className="space-y-6 text-light-muted leading-relaxed">
                <p className="text-lg">
                  <strong className="text-light-text">PLADUREX</strong> es una empresa especializada en la <Link to="/servicios" className="text-primary-orange hover:underline">instalación de pladur en Sabadell</Link> y toda la comarca del Vallès. Ofrecemos soluciones profesionales para <Link to="/servicios#tabiques" className="text-primary-orange hover:underline">tabiques</Link>, <Link to="/servicios#trasdosados" className="text-primary-orange hover:underline">trasdosados</Link>, <Link to="/servicios#techos" className="text-primary-orange hover:underline">falsos techos</Link> y sistemas de <Link to="/servicios#acustico" className="text-primary-orange hover:underline">aislamiento acústico</Link> y <Link to="/servicios#termico" className="text-primary-orange hover:underline">térmico</Link>.
                </p>
                <p className="text-lg">
                  Trabajamos tanto en <strong className="text-light-text">viviendas particulares</strong> como en <strong className="text-light-text">oficinas y locales comerciales</strong>, adaptándonos a las necesidades específicas de cada proyecto. Nuestra experiencia nos permite ofrecer asesoramiento técnico y soluciones personalizadas.
                </p>
                <p className="text-lg">
                  Nos caracterizamos por la <strong className="text-light-text">calidad de nuestros acabados</strong>, el cumplimiento de plazos y la limpieza en la ejecución. Utilizamos materiales de primera calidad y aplicamos las técnicas más actuales del sector. ¿Tienes dudas? Consulta nuestras <Link to="/faq" className="text-primary-orange hover:underline">preguntas frecuentes</Link>.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Nuestros <span className="text-primary-orange">valores</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Principios que guían nuestro trabajo en cada proyecto
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-dark-bg p-8 rounded-xl border border-dark-lighter hover:border-primary-orange/50 transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-6">
                  <i className={`${value.icon} text-3xl text-primary-orange`}></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-light-muted leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-dark-bg">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              Cómo <span className="text-primary-orange">trabajamos</span>
            </h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Metodología clara y transparente en cada fase del proyecto
            </p>
          </motion.div>

          <div className="max-w-5xl mx-auto space-y-8">
            {process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-dark-card p-8 rounded-xl border border-dark-lighter hover:border-primary-orange/50 transition-all"
              >
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary-orange/10 rounded-lg">
                      <i className={`${step.icon} text-3xl text-primary-orange`}></i>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-3">
                      <span className="text-4xl font-display font-bold text-primary-orange/30">{step.number}</span>
                      <h3 className="text-2xl font-semibold">{step.title}</h3>
                    </div>
                    <p className="text-light-muted leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Method */}
      <section className="py-20 bg-dark-card">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
                Trabajamos en <span className="text-primary-orange">espacios habitados</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-home-smile-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Coordinación</h3>
                <p className="text-light-muted leading-relaxed">
                  Adaptamos los horarios de trabajo a tus necesidades. Coordinamos con otros gremios si es necesario y minimizamos las molestias durante la instalación.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-broom-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Limpieza diaria</h3>
                <p className="text-light-muted leading-relaxed">
                  Recogemos y retiramos los escombros al finalizar cada jornada. Protegemos suelos y muebles. Dejamos el espacio ordenado y limpio.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-file-text-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Facturación</h3>
                <p className="text-light-muted leading-relaxed">
                  Emitimos factura con todos los detalles del trabajo realizado. Presupuesto cerrado sin sorpresas. Formas de pago flexibles.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-dark-bg p-6 rounded-xl border border-dark-lighter"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-orange/10 rounded-lg mb-4">
                  <i className="ri-medal-line text-2xl text-primary-orange"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Garantía</h3>
                <p className="text-light-muted leading-relaxed">
                  Garantizamos la calidad de nuestro trabajo. Utilizamos materiales homologados y aplicamos las técnicas correctas en cada instalación.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-orange to-primary-orange-light">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6 text-white">
              ¿Hablamos de tu proyecto?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Contacta con nosotros para recibir asesoramiento personalizado y un presupuesto sin compromiso
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="tel:+34629501941"
                className="flex items-center space-x-2 px-8 py-4 bg-white text-primary-orange rounded-lg font-semibold text-lg hover:shadow-xl transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>629 501 941</span>
              </a>
              <Link
                to="/contacto"
                className="flex items-center space-x-2 px-8 py-4 border-2 border-white text-white rounded-lg font-semibold text-lg hover:bg-white hover:text-primary-orange transition-all whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                <span>Formulario de contacto</span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
      <ScrollToTopButton />
    </div>
  );
};

export default EmpresaPage;
